import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { SERVICE_CATEGORIES } from "@/lib/constants";
import { ChevronLeft, ChevronRight, Play, Video, X } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [canScrollStrip, setCanScrollStrip] = useState(false);
  const filmstripRef = useRef(null);

  useEffect(() => {
    api
      .get("/gallery", { params: category === "all" ? {} : { category } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [category]);

  // Split into videos (filmstrip) + images (grid). Lightbox iterates all.
  const videos = useMemo(
    () => items.filter((it) => it.media_type === "video" && it.video_url),
    [items]
  );
  const images = useMemo(
    () => items.filter((it) => !(it.media_type === "video" && it.video_url)),
    [items]
  );
  // Order used by lightbox nav: videos first, then images (matches on-page reading order)
  const ordered = useMemo(() => [...videos, ...images], [videos, images]);

  const openLightbox = (it) => {
    const idx = ordered.findIndex((x) => x.id === it.id);
    setLightboxIdx(idx >= 0 ? idx : 0);
  };
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const nextItem = useCallback(() => {
    setLightboxIdx((i) => (i < 0 ? -1 : (i + 1) % ordered.length));
  }, [ordered.length]);
  const prevItem = useCallback(() => {
    setLightboxIdx((i) => (i < 0 ? -1 : (i - 1 + ordered.length) % ordered.length));
  }, [ordered.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") nextItem();
      else if (e.key === "ArrowLeft") prevItem();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, nextItem, prevItem]);

  const scrollFilmstrip = (dir) => {
    const el = filmstripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  // Track filmstrip overflow so we can disable arrows when everything fits on screen.
  useEffect(() => {
    const el = filmstripRef.current;
    if (!el) {
      setCanScrollStrip(false);
      return;
    }
    const update = () => setCanScrollStrip(el.scrollWidth > el.clientWidth + 4);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [videos.length]);

  const active = lightboxIdx >= 0 ? ordered[lightboxIdx] : null;
  const isActiveVideo = active?.media_type === "video" && active?.video_url;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="gallery-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Media Vault</div>
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-none tracking-tighter">
        The <span className="brand-blue">gallery</span>.
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl">
        Action shots, drills, corporate tournaments, and academy moments from every corner of the complex.
      </p>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
        {SERVICE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`gallery-filter-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-all ${
              category === c.key
                ? "bg-brand-blue text-white border-brand-blue"
                : "border-white/20 text-white/70 hover:border-white/50"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {videos.length > 0 && (
        <section className="mt-12" data-testid="gallery-filmstrip-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange mb-1">// Reel</div>
              <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter">
                Video <span className="brand-blue">filmstrip</span>
              </h2>
            </div>
            <div className="hidden md:flex gap-2">
              <button
                data-testid="filmstrip-prev"
                onClick={() => scrollFilmstrip(-1)}
                disabled={!canScrollStrip}
                className="p-2 border border-white/20 hover:border-brand-blue hover:text-brand-blue transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/20 disabled:hover:text-inherit"
                aria-label="Scroll filmstrip left"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="filmstrip-next"
                onClick={() => scrollFilmstrip(1)}
                disabled={!canScrollStrip}
                className="p-2 border border-white/20 hover:border-brand-blue hover:text-brand-blue transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/20 disabled:hover:text-inherit"
                aria-label="Scroll filmstrip right"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div
            ref={filmstripRef}
            data-testid="gallery-filmstrip"
            className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-thin"
            style={{ scrollbarWidth: "thin" }}
          >
            {videos.map((v) => (
              <button
                key={v.id}
                data-testid={`gallery-video-${v.id}`}
                onClick={() => openLightbox(v)}
                className="relative shrink-0 snap-start w-64 md:w-80 aspect-video border border-white/10 group overflow-hidden"
              >
                {v.image_url ? (
                  <img
                    src={v.image_url}
                    alt={v.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <video
                    src={v.video_url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-brand-blue/90 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play size={22} className="text-white ml-1" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute top-2 left-2">
                  <span className="bg-brand-orange text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                    <Video size={10} /> Video
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">
                    {(v.category || "").replace("_", " ")}
                  </div>
                  <div className="text-sm font-bold mt-1 line-clamp-1">{v.title}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
        {images.map((it, i) => (
          <button
            key={it.id}
            data-testid={`gallery-item-${it.id}`}
            onClick={() => openLightbox(it)}
            className={`relative overflow-hidden border border-white/10 group ${i % 5 === 0 ? "md:row-span-2" : ""}`}
          >
            <img
              src={it.image_url}
              alt={it.title}
              className="w-full h-full object-cover aspect-square transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
              <div className="text-left">
                <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">
                  {(it.category || "").replace("_", " ")}
                </div>
                <div className="text-sm font-bold mt-1">{it.title}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-24 text-white/40 font-mono uppercase tracking-widest" data-testid="gallery-empty">
          No media yet.
        </div>
      )}

      {active && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            data-testid="gallery-close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
            aria-label="Close"
          >
            <X size={28} />
          </button>

          {ordered.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevItem();
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 border border-white/20 text-white/70 hover:text-white hover:border-white/60 bg-black/40 z-10"
                aria-label="Previous"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextItem();
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 border border-white/20 text-white/70 hover:text-white hover:border-white/60 bg-black/40 z-10"
                aria-label="Next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <div
            className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {isActiveVideo ? (
              <video
                key={active.id}
                src={active.video_url}
                poster={active.image_url || undefined}
                controls
                autoPlay
                playsInline
                className="max-h-[78vh] w-full object-contain bg-black"
                data-testid="lightbox-video"
              />
            ) : (
              <img
                key={active.id}
                src={active.image_url}
                alt={active.title}
                className="max-h-[78vh] object-contain"
                data-testid="lightbox-image"
              />
            )}
            <div className="mt-4 text-center">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-1">
                {(active.category || "").replace("_", " ")}
                {isActiveVideo && <span className="ml-2 brand-orange">• video</span>}
              </div>
              <div className="font-display text-2xl uppercase" data-testid="lightbox-title">
                {active.title}
              </div>
              {active.caption && (
                <div className="text-white/60 text-sm mt-1" data-testid="lightbox-caption">
                  {active.caption}
                </div>
              )}
              {ordered.length > 1 && (
                <div className="text-white/40 text-xs font-mono mt-3" data-testid="lightbox-counter">
                  {lightboxIdx + 1} / {ordered.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
